import type { CompletedTradeData, TradeItem, TradeType, TradeResponse } from './types'

/** Normalize API trade payload into UI shape with split store/customer lines. */
export function fromTradeApiResponse(trade: TradeType): TradeType {
  const items = trade.items || []
  return {
    ...trade,
    items,
    storeItems: items.filter((item: TradeItem) => item.side === 'store'),
    customerItems: items.filter((item: TradeItem) => item.side === 'customer'),
  }
}

/** Map completed trade API response to receipt / print payload. */
export function mapTradeToCompletedData(trade: TradeResponse): CompletedTradeData {
  const items = trade.items || []
  const storeItems = trade.storeItems ?? items.filter((item: TradeItem) => item.side === 'store')
  const customerItems = trade.customerItems ?? items.filter((item: TradeItem) => item.side === 'customer')

  return {
    id: trade.id,
    type: trade.type,
    receiptNumber: trade.receiptNumber ?? null,
    customerName: trade.customerName || '',
    customerEmail: trade.customerEmail || '',
    customerPhone: trade.customerPhone || '',
    storeValueCents: trade.storeValueCents,
    customerValueCents: trade.customerValueCents,
    differenceValueCents: trade.differenceValueCents,
    storeItems,
    customerItems,
    paymentMethod: trade.paymentMethod || 'none',
    completedAt: trade.completedAt || new Date().toISOString(),
    notes: trade.notes || '',
  }
}

/** POST /trades/:id/complete body from current draft totals. */
export function buildCompleteTradePayload(
  differenceValueCents: number,
  paymentMethod: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { paymentMethod }
  if (differenceValueCents !== 0) {
    payload.paymentAmountCents = Math.abs(differenceValueCents)
  }
  return payload
}

/** Line value when applying bulk customer % of market (matches server PATCH bulk-percent). */
export function customerLineValueFromBulkPercent(marketValueCents: number, percent: number): number {
  const clamped = Math.max(0, Math.min(1000, percent))
  return Math.max(0, Math.round((marketValueCents * clamped) / 100))
}
